import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * A reusable hook to elegantly manage transient visual highlights for list or table rows 
 * (e.g., following a socket CREATE/UPDATE event).
 *
 * @param {number} duration - The duration in ms matching the CSS fade animation time.
 * @returns {Object} { highlightedRowIds, highlightRow, getHighlightClass }
 */
export default function useVisualHighlight(duration = 2500) {
    // Store as Map<id, { type: string }> to differentiate styles (CREATE vs UPDATE)
    const [highlightedRows, setHighlightedRows] = useState(() => new Map());
    const highlightTimeoutsRef = useRef(new Map());

    // Clean up all timeouts when the component unmounts
    useEffect(() => {
        return () => {
            for (const timeoutId of highlightTimeoutsRef.current.values()) {
                clearTimeout(timeoutId);
            }
            highlightTimeoutsRef.current.clear();
        };
    }, []);

    const highlightRow = useCallback((id, type = 'CREATE') => {
        if (!id) return;

        // Cancel any existing timeout for this specific row so it restarts
        const existingTimeout = highlightTimeoutsRef.current.get(id);
        if (existingTimeout) clearTimeout(existingTimeout);

        const applyHighlight = () => {
            // Add the ID with its specific action type
            setHighlightedRows(prev => {
                const next = new Map(prev);
                next.set(id, { type });
                return next;
            });

            // Schedule removal of the highlight class
            const timeoutId = setTimeout(() => {
                setHighlightedRows(prev => {
                    const next = new Map(prev);
                    next.delete(id);
                    return next;
                });
                highlightTimeoutsRef.current.delete(id);
            }, duration);

            highlightTimeoutsRef.current.set(id, timeoutId);
        };

        if (existingTimeout) {
            // Remove it first to allow the DOM to reset the CSS animation
            setHighlightedRows(prev => {
                const next = new Map(prev);
                next.delete(id);
                return next;
            });
            // Re-apply after a small delay so React commits the removal first
            const resetTimeoutId = setTimeout(applyHighlight, 10);
            highlightTimeoutsRef.current.set(id, resetTimeoutId);
        } else {
            applyHighlight();
        }
    }, [duration]);

    /**
     * Helper to return the standardized highlight class 
     * based on the recorded action type.
     */
    const getHighlightClass = useCallback((id, baseClassName = 'socket-highlight-target') => {
        const highlightEntry = highlightedRows.get(id);
        if (!highlightEntry) return '';
        
        // If it's an UPDATE, swap "-target" with "-info" (or append "-info" if no "-target")
        if (highlightEntry.type === 'UPDATE') {
            return baseClassName.includes('target') 
                ? baseClassName.replace('target', 'info') 
                : `${baseClassName}-info`;
        }
        
        return baseClassName;
    }, [highlightedRows]);

    return { getHighlightClass, highlightRow }; // Removed highlightedRowIds Set export to force clean encapsulation
}
